import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { parseISO, format } from 'date-fns';
import type { LessonSession } from './types';
import styles from './ProgressCharts.module.css';

interface TimeTrendChartProps {
  sessions: LessonSession[];
}

interface DataPoint {
  index: number;
  date: string;
  displayDate: string;
  avgTime: number;
  rollingAvg: number | null;
}

export function TimeTrendChart({ sessions }: TimeTrendChartProps) {
  const [viewMode, setViewMode] = useState<'recent' | 'all'>('recent');

  const chartData = useMemo(() => {
    if (sessions.length === 0) return [];

    // Sort sessions chronologically (oldest first)
    const sorted = [...sessions].sort(
      (a, b) => new Date(a.completed_at).getTime() - new Date(b.completed_at).getTime()
    );

    // Take recent 20 or all based on view mode
    const toDisplay = viewMode === 'recent' ? sorted.slice(-20) : sorted;

    // Calculate rolling average (5-session window)
    const windowSize = 5;
    const data: DataPoint[] = toDisplay.map((session, idx) => {
      const date = parseISO(session.completed_at);

      // Calculate rolling average for sessions up to this point
      let rollingAvg: number | null = null;
      if (idx >= windowSize - 1) {
        const windowStart = idx - windowSize + 1;
        const windowSessions = toDisplay.slice(windowStart, idx + 1);
        rollingAvg = windowSessions.reduce((sum, s) => sum + s.average_time, 0) / windowSize;
      }

      return {
        index: idx + 1,
        date: format(date, 'yyyy-MM-dd'),
        displayDate: format(date, 'MMM dd'),
        avgTime: Math.round(session.average_time * 10) / 10,
        rollingAvg: rollingAvg ? Math.round(rollingAvg * 10) / 10 : null,
      };
    });

    return data;
  }, [sessions, viewMode]);

  const overallAvgTime = useMemo(() => {
    if (sessions.length === 0) return 0;
    return sessions.reduce((sum, s) => sum + s.average_time, 0) / sessions.length;
  }, [sessions]);

  // Calculate Y-axis domain based on data
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 10];
    const times = chartData.map((d) => d.avgTime);
    const maxTime = Math.max(...times);
    const roundedMax = Math.ceil(maxTime / 5) * 5; // Round up to nearest 5
    return [0, Math.max(roundedMax, 10)];
  }, [chartData]);

  if (sessions.length === 0) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>Time per Card</h3>
        </div>
        <div className={styles.emptyState}>No data yet</div>
      </div>
    );
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div>
          <h3 className={styles.chartTitle}>Time per Card</h3>
          <p className={styles.chartSubtitle}>Avg: {overallAvgTime.toFixed(1)}s</p>
        </div>
        <select
          className={styles.timelineSelect}
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value as 'recent' | 'all')}
        >
          <option value="recent">Last 20</option>
          <option value="all">All time</option>
        </select>
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className={styles.gridLine} />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              interval={viewMode === 'recent' ? 4 : 'preserveStartEnd'}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              domain={yDomain}
              tickFormatter={(value) => `${value}s`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value, name) => [
                `${value}s`,
                name === 'avgTime' ? 'Time/card' : '5-session avg',
              ]}
              labelFormatter={(label) => label}
            />
            <ReferenceLine y={overallAvgTime} stroke="#94a3b8" strokeDasharray="5 5" />
            <Line
              type="monotone"
              dataKey="avgTime"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="rollingAvg"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className={styles.chartLegend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: '#3b82f6' }} />
          Time/card
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: '#f59e0b' }} />
          5-session avg
        </span>
      </div>
    </div>
  );
}
